import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import LockIcon from "@mui/icons-material/LockOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const PersonCell = ({ row }) => (
  <Box textAlign="left" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Typography variant="button" fontWeight={600}>
      {row.original.personName}
    </Typography>
    {row.original.confidential && <LockIcon fontSize="inherit" color="warning" />}
  </Box>
);
PersonCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const RequestCell = ({ value }) => (
  <Typography variant="body2" sx={{ maxWidth: 280 }} noWrap title={value}>
    {value}
  </Typography>
);
RequestCell.propTypes = { value: PropTypes.string.isRequired };

const PRAYER_STATUS_LABELS = { open: "Open", praying: "Praying", answered: "Answered" };
const StatusCell = ({ value }) => <StatusChip value={PRAYER_STATUS_LABELS[value] || value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const TYPE_LABELS = {
  hospital: "Hospital",
  home: "Home",
  counseling: "Counseling",
  other: "Other",
};
const TypeCell = ({ value }) => <StatusChip value={TYPE_LABELS[value] || value} color="info" />;
TypeCell.propTypes = { value: PropTypes.string.isRequired };

const PrayerActionsCell = ({ row, handleAdvance, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    {row.original.status !== "answered" && (
      <Button
        size="small"
        variant="outlined"
        color="success"
        onClick={() => handleAdvance(row.original)}
      >
        {row.original.status === "open" ? "Start praying" : "Mark answered"}
      </Button>
    )}
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
PrayerActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleAdvance: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

const VisitActionsCell = ({ row, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
VisitActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleDelete: PropTypes.func.isRequired,
};

const emptyPrayerForm = () => ({
  member_id: null,
  memberLabel: "",
  requester_name: "",
  request: "",
  confidential: false,
});

const emptyVisitForm = () => ({
  member_id: null,
  memberLabel: "",
  visit_date: new Date().toISOString().split("T")[0],
  type: "home",
  notes: "",
  confidential: false,
});

export default function usePastoralCareData() {
  const { church } = useAuth();

  const [prayerRows, setPrayerRows] = useState([]);
  const [visitRows, setVisitRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [prayerModalOpen, setPrayerModalOpen] = useState(false);
  const [visitModalOpen, setVisitModalOpen] = useState(false);
  const [prayerForm, setPrayerForm] = useState(emptyPrayerForm());
  const [visitForm, setVisitForm] = useState(emptyVisitForm());
  const [isSaving, setIsSaving] = useState(false);

  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMemberLabel, setSelectedMemberLabel] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const handleAdvance = useCallback(async (row) => {
    const next = row.status === "open" ? "praying" : "answered";
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/prayer-requests/${row.id}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchPrayerRequests();
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
    // eslint-disable-next-line
  }, []);

  const handleDeletePrayer = useCallback(async (row) => {
    const result = await Swal.fire({
      title: "Delete this prayer request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/prayer-requests/${row.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchPrayerRequests();
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
    // eslint-disable-next-line
  }, []);

  const handleDeleteVisit = useCallback(async (row) => {
    const result = await Swal.fire({
      title: "Delete this visit record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/visits/${row.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchVisits();
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    }
    // eslint-disable-next-line
  }, []);

  const prayerColumns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Person", accessor: "personName", align: "left", width: "18%", Cell: PersonCell },
    { Header: "Request", accessor: "request", align: "left", width: "32%", Cell: RequestCell },
    { Header: "Status", accessor: "status", align: "center", width: "12%", Cell: StatusCell },
    { Header: "Date", accessor: "dateLabel", align: "center", width: "13%" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "20%",
      Cell: (props) => (
        <PrayerActionsCell
          {...props}
          handleAdvance={handleAdvance}
          handleDelete={handleDeletePrayer}
        />
      ),
    },
  ];

  const visitColumns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Member", accessor: "personName", align: "left", width: "22%", Cell: PersonCell },
    { Header: "Type", accessor: "type", align: "center", width: "13%", Cell: TypeCell },
    { Header: "Date", accessor: "dateLabel", align: "center", width: "13%" },
    { Header: "Visited by", accessor: "visitedByName", align: "left", width: "17%" },
    { Header: "Notes", accessor: "notes", align: "left", width: "15%", Cell: RequestCell },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "15%",
      Cell: (props) => <VisitActionsCell {...props} handleDelete={handleDeleteVisit} />,
    },
  ];

  const fetchPrayerRequests = useCallback(async () => {
    if (!church?.id) return;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/prayer-requests`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const requests = result.data || [];
      setPrayerRows(
        requests.map((r, index) => ({
          id: r.id,
          serial: index + 1,
          personName: r.member
            ? `${r.member.first_name} ${r.member.surname}`
            : r.requester_name || "—",
          request: r.request,
          status: r.status,
          confidential: r.confidential,
          dateLabel: new Date(r.created_at).toLocaleDateString(),
          originalData: r,
        }))
      );
    } catch (e) {
      console.error("Fetch prayer requests failed:", e);
      setError(e.message);
    }
  }, [church?.id]);

  const fetchVisits = useCallback(async () => {
    if (!church?.id) return;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/visits`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const visits = result.data || [];
      setVisitRows(
        visits.map((v, index) => ({
          id: v.id,
          serial: index + 1,
          personName: v.member ? `${v.member.first_name} ${v.member.surname}` : "—",
          type: v.type,
          notes: v.notes || "—",
          confidential: v.confidential,
          dateLabel: new Date(v.visit_date).toLocaleDateString(),
          visitedByName: v.visited_by?.name || v.visitedBy?.name || "—",
          originalData: v,
        }))
      );
    } catch (e) {
      console.error("Fetch visits failed:", e);
      setError(e.message);
    }
  }, [church?.id]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchPrayerRequests(), fetchVisits()]).finally(() => setIsLoading(false));
  }, [fetchPrayerRequests, fetchVisits]);

  const searchMembers = async (query) => {
    if (!church?.id || !query.trim()) {
      setMemberResults([]);
      return;
    }
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setMemberResults(result.data || []);
    } catch (e) {
      console.error("Member search failed:", e);
      setMemberResults([]);
    }
  };

  useEffect(() => {
    if (!memberSearch.trim() || memberSearch === selectedMemberLabel) {
      setMemberResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(memberSearch), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [memberSearch]);

  const handleOpenPrayerModal = () => {
    setPrayerForm(emptyPrayerForm());
    setMemberSearch("");
    setMemberResults([]);
    setSelectedMemberLabel("");
    setPrayerModalOpen(true);
  };

  const handleOpenVisitModal = () => {
    setVisitForm(emptyVisitForm());
    setMemberSearch("");
    setMemberResults([]);
    setSelectedMemberLabel("");
    setVisitModalOpen(true);
  };

  const handleSelectPrayerMember = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setPrayerForm((prev) => ({ ...prev, member_id: member.id, memberLabel: label }));
    setSelectedMemberLabel(label);
    setMemberSearch(label);
    setMemberResults([]);
  };

  const handleSelectVisitMember = (member) => {
    const label = `${member.first_name} ${member.surname}`;
    setVisitForm((prev) => ({ ...prev, member_id: member.id, memberLabel: label }));
    setSelectedMemberLabel(label);
    setMemberSearch(label);
    setMemberResults([]);
  };

  const handleSubmitPrayer = async () => {
    try {
      if (!prayerForm.request.trim()) throw new Error("Enter the prayer request.");
      if (!prayerForm.member_id && !prayerForm.requester_name.trim()) {
        throw new Error("Pick a member or enter a name for the requester.");
      }
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/prayer-requests`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          member_id: prayerForm.member_id || null,
          requester_name: prayerForm.member_id ? null : prayerForm.requester_name,
          request: prayerForm.request,
          confidential: prayerForm.confidential,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({ title: "Success!", text: "Prayer request recorded", icon: "success" });
      fetchPrayerRequests();
      setPrayerModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitVisit = async () => {
    try {
      if (!visitForm.member_id) throw new Error("Pick the member who was visited.");
      if (!visitForm.visit_date) throw new Error("Pick a visit date.");
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/pastoral-care/visits`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          member_id: visitForm.member_id,
          visit_date: visitForm.visit_date,
          type: visitForm.type,
          notes: visitForm.notes || null,
          confidential: visitForm.confidential,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({ title: "Success!", text: "Visit recorded", icon: "success" });
      fetchVisits();
      setVisitModalOpen(false);
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = () => window.print();

  return {
    prayerColumns,
    prayerRows,
    visitColumns,
    visitRows,
    isLoading,
    error,
    handlePrint,
    prayerModalOpen,
    setPrayerModalOpen,
    visitModalOpen,
    setVisitModalOpen,
    prayerForm,
    setPrayerForm,
    visitForm,
    setVisitForm,
    handleOpenPrayerModal,
    handleOpenVisitModal,
    handleSubmitPrayer,
    handleSubmitVisit,
    isSaving,
    memberSearch,
    setMemberSearch,
    memberResults,
    handleSelectPrayerMember,
    handleSelectVisitMember,
  };
}
